/**
 * Where the team is today, for the manager they report to.
 *
 * The counts are the point: a manager wants "two people are late and one has
 * not appeared" before they want a list. The list is what you open once a count
 * looks wrong, so it is filtered by clicking the count rather than by a separate
 * control.
 *
 * Status is a precedence decided by the server, not a set of badges assembled
 * here — see backend/team_status_service. Facts that coexist with any status
 * (late, a short leave, the times themselves) render as detail on the row.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { teamAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const STATUS_META = {
  working: { label: 'Logged in', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900' },
  not_logged_in: { label: 'Not logged in yet', tone: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900' },
  on_leave: { label: 'On leave', tone: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900' },
  half_day: { label: 'Half day', tone: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900' },
  week_off: { label: 'Week off', tone: 'bg-muted text-muted-foreground border-border' },
};

// Order is the reading order a manager scans in: what needs action first.
const CHIPS = [
  { key: 'late', label: 'Late', filter: (m) => m.is_late },
  { key: 'not_logged_in', label: 'Not logged in', filter: (m) => m.status === 'not_logged_in' },
  { key: 'working', label: 'Logged in', filter: (m) => m.status === 'working' },
  { key: 'half_day', label: 'Half day', filter: (m) => m.status === 'half_day' },
  { key: 'short_leave', label: 'Short leave', filter: (m) => m.on_short_leave },
  { key: 'on_leave', label: 'On leave', filter: (m) => m.status === 'on_leave' },
  { key: 'maternity_leave', label: 'Maternity', filter: (m) => m.is_maternity_leave },
  { key: 'week_off', label: 'Week off', filter: (m) => m.status === 'week_off' },
  { key: 'with_upcoming_leave', label: 'Upcoming leave', filter: (m) => m.upcoming_leaves?.length },
];

const timeOf = (iso) => (iso ? String(iso).slice(11, 16) : null);
const dayOf = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

const initials = (name) =>
  String(name || '?').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

const TeamStatusBoard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await teamAPI.getTeamStatus();
      setData(payload);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load your team'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const members = useMemo(() => {
    const rows = data?.members || [];
    const chip = CHIPS.find((c) => c.key === active);
    return chip ? rows.filter(chip.filter) : rows;
  }, [data, active]);

  if (loading && !data) {
    return <div className="rounded-xl border border-border p-6 text-sm text-muted-foreground">Loading your team…</div>;
  }
  if (error) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm flex items-center gap-2">
        <AlertCircle className="h-4 w-4 shrink-0" /> {error}
      </div>
    );
  }
  // An employee with nobody reporting to them gets no board rather than an
  // empty one: the server answers by reporting line, so this is "not a manager".
  if (!data?.counts?.team_size) return null;

  return (
    <section className="rounded-xl border border-border bg-card">
      <header className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold">Today’s team</h3>
          <p className="text-xs text-muted-foreground">
            {data.counts.team_size} direct report{data.counts.team_size === 1 ? '' : 's'} · {dayOf(data.date)}
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-60">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </header>

      <div className="flex flex-wrap gap-2 px-4 py-3">
        {CHIPS.map((chip) => {
          const count = data.counts[chip.key] || 0;
          const selected = active === chip.key;
          return (
            <button key={chip.key} type="button" disabled={!count}
              onClick={() => setActive(selected ? null : chip.key)}
              className={`rounded-lg border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-40 disabled:cursor-default ${
                selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted'
              }`}>
              <span className="font-semibold">{count}</span> <span>{chip.label}</span>
            </button>
          );
        })}
      </div>

      <ul className="divide-y divide-border border-t border-border">
        {members.map((m) => {
          const meta = STATUS_META[m.status] || STATUS_META.week_off;
          return (
            <li key={m.employee_id} className="flex items-center gap-3 px-4 py-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold">
                {initials(m.name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">{m.name}</span>
                  <span className={`rounded-md border px-1.5 py-0.5 text-[11px] ${meta.tone}`}>{meta.label}</span>
                  {m.is_late && (
                    <span className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[11px] text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
                      <Clock className="h-3 w-3" /> {m.late_minutes} min late
                    </span>
                  )}
                  {m.on_short_leave && (
                    <span className="rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      Short leave {timeOf(`T${m.short_leave_from}`) || m.short_leave_from?.slice(0, 5)}–{m.short_leave_to?.slice(0, 5)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {[
                    m.designation,
                    m.checked_in_at && `in ${timeOf(m.checked_in_at)}`,
                    m.checked_out_at && `out ${timeOf(m.checked_out_at)}`,
                    m.leave_type && `${m.leave_type}${m.leave_ends_on ? ` to ${dayOf(m.leave_ends_on)}` : ''}`,
                    m.upcoming_leaves?.length && `next leave ${dayOf(m.upcoming_leaves[0].start_date)}`,
                  ].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
            </li>
          );
        })}
        {!members.length && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">Nobody in this group today.</li>
        )}
      </ul>
    </section>
  );
};

export default TeamStatusBoard;
